import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useAuth } from '../../hooks/useAuth'
import { setModal } from '../../slices/modalSlice'
import { GenericModal } from '../reutilizables/modal/GenericModal'
import * as datosFiscalesService from '../servicios/datosFiscalesService'
import * as domiciliosService from '../servicios/domiciliosService'
import * as usuariosEBPService from '../servicios/usuariosEBPService'
import * as usuarioService from '../servicios/usuarioService'
import styles from './DatosFiscales.module.css'
import stylesPerfil from './Perfil.module.css'

const DatosFiscales = () => {
  const {
    loginDataContext: {
      idUsuario,
      empid,
      aceptaNoHayDatosFiscales,
      existeDatosFiscales,
      aceptaTyC,
      esPrimerAcceso
    } = {},
    updateUserData,
    esPerfilAdmin,
    esAdminEBP
  } = useAuth()

  const [datosFiscales, setDatosFiscales] = useState([])
  const [provincias, setProvincias] = useState([])
  const [tiposFiscales, setTiposFiscales] = useState([])

  const dispatch = useDispatch()

  const { values, errores: erroresModal, editMode } = useSelector((state) => state.genericModal)
  const noEsAdmin = !esPerfilAdmin && !esAdminEBP

  const obtenerDatosFiscales = async () => {
    const { data } = await datosFiscalesService.buscarTodos(empid)
    if (data.length === 0) {
      updateUserData({ existeDatosFiscales: false })
    }
    setDatosFiscales(data)
  }

  const obtenerTiposFiscales = async () => {
    const { data } = await usuarioService.obtenerTiposFiscales()
    setTiposFiscales(data.map((t) => ({ value: t.idCondicionFrenteIIBB, label: t.tipo })))
  }

  const obtenerProvincias = async () => {
    const { data } = await domiciliosService.BuscarPorPais(1)
    setProvincias(data)
  }

  const campos = [
    {
      type: 'select',
      name: 'provincia',
      placeholder: 'Seleccioná una',
      label: 'Provincia',
      isFullWidth: true,
      isEditable: false,
      isGrid: false,
      options: provincias.map((p) => ({
        value: p.idProvincia,
        label: p.nombre,
        key: p.idProvincia
      }))
    },
    {
      type: 'select',
      name: 'condicionFrenteIIBB',
      placeholder: 'Seleccionar',
      label: 'Condición frente a IIBB',
      isFullWidth: true,
      isEditable: true,
      isGrid: false,
      options: tiposFiscales.map((t) => ({
        value: t.value,
        label: t.label,
        key: t.value
      }))
    },
    {
      type: 'select',
      name: 'esAgente',
      placeholder: 'Seleccionar',
      label: 'Es agente de percepción',
      isFullWidth: true,
      isEditable: true,
      isGrid: false,
      options: [
        { value: 'true', label: 'Sí', key: 'agente-true' },
        { value: 'false', label: 'No', key: 'agente-false' }
      ]
    }
  ]

  const abrirModal = () => {
    dispatch(
      setModal({
        size: 'sm',
        title: 'Agregar dato fiscal',
        type: 'form',
        body: '',
        show: true,
        modalId: 'datos-fiscales',
        editMode: false,
        values: {
          provincia: '',
          condicionFrenteIIBB: '',
          esAgente: ''
        },
        campos
      })
    )
  }

  const guardarDatoFiscal = async () => {
    try {
      const nuevoDatoFiscal = {
        idUsuario,
        idProvincia: parseInt(values.provincia),
        idCondicionFrenteIIBB: parseInt(values.condicionFrenteIIBB),
        esAgentePercepcion: values.esAgente === 'true',
        empid: empid
      }

      await datosFiscalesService.insertar(nuevoDatoFiscal)
      obtenerDatosFiscales()
    } catch (error) {
      const mensaje = `Ocurrió un error al guardar el dato fiscal.`
      toastErrorLog(error, mensaje, 'datosFiscales')
      const mensajeError =
        error.response?.data?.message || error.response?.data || 'Error al guardar el dato fiscal.'

      const errorFinal =
        typeof mensajeError === 'string' ? mensajeError : 'Error al guardar el dato fiscal.'

      dispatch(
        setModal({
          errores: { general: errorFinal },
          type: 'form'
        })
      )
      throw error
    }
  }

  const eliminarDatoFiscal = async (idDatoFiscal) => {
    try {
      await datosFiscalesService.eliminar(idDatoFiscal)
      obtenerDatosFiscales()
    } catch (error) {
      const mensaje = `Ocurrió un error al eliminar el dato fiscal.`
      toastErrorLog(error, mensaje, 'datosFiscales')
    }
  }

  const handleActualizar = async () => {
    if (
      datosFiscales.find(
        (d) =>
          d.idProvincia === parseInt(values.provincia) &&
          d.idCondicionFrenteIIBB === parseInt(values.condicionFrenteIIBB) &&
          d.esAgentePercepcion === (values.esAgente === 'true') &&
          (!editMode || d.idDatoFiscal !== values.idDatoFiscal)
      )
    ) {
      const mensajeError =
        'Ya existe una percepción para la provincia, condición y agente ingresados.'

      dispatch(
        setModal({
          errores: { general: mensajeError },
          type: 'form'
        })
      )
      throw new Error(mensajeError)
    }
    try {
      const datoActualizado = {
        idDatoFiscal: values.idDatoFiscal,
        idCondicionFrenteIIBB: parseInt(values.condicionFrenteIIBB),
        esAgentePercepcion: values.esAgente === 'true'
      }

      await datosFiscalesService.actualizar(datoActualizado)
      obtenerDatosFiscales()
    } catch (error) {
      const mensaje = `Ocurrió un error al actualizar el dato fiscal.`
      toastErrorLog(error, mensaje, 'datosFiscales')

      const mensajeError =
        error.response?.data?.message ||
        error.response?.data ||
        'Error al actualizar el dato fiscal.'

      const errorFinal =
        typeof mensajeError === 'string' ? mensajeError : 'Error al actualizar el dato fiscal.'
      dispatch(
        setModal({
          errores: { general: errorFinal },
          type: 'form'
        })
      )
      throw error
    }
  }

  useEffect(() => {
    if (!existeDatosFiscales && !aceptaNoHayDatosFiscales && aceptaTyC && !esPrimerAcceso) {
      const mensaje = noEsAdmin
        ? 'Por favor, comunicate con el administrador para que ingrese los datos fiscales o acepte las condiciones'
        : 'Por favor, ingresá datos fiscales o aceptá las condiciones'
      dispatch(
        setModal({
          size: 'sm',
          title: 'Completá tus datos fiscales',
          modalId: 'datos-fiscales',
          type: 'jsx',
          body: mensaje,
          show: true,
          editMode: false,
          campos
        })
      )
    }
  }, [])

  const editarDatoFiscal = (dato) => {
    dispatch(
      setModal({
        size: 'sm',
        title: 'Editar dato fiscal',
        type: 'form',
        body: '',
        show: true,
        modalId: 'datos-fiscales',
        editMode: true,
        values: {
          idDatoFiscal: dato.idDatoFiscal,
          provincia: dato.idProvincia,
          condicionFrenteIIBB: dato.idCondicionFrenteIIBB,
          esAgente: dato.esAgentePercepcion ? 'true' : 'false'
        },
        campos
      })
    )
  }

  useEffect(() => {
    if (idUsuario) {
      obtenerDatosFiscales()
    }
    obtenerProvincias()
    obtenerTiposFiscales()
  }, [idUsuario])

  const handleChangeModal = (e) => {
    const { name, value } = e.target
    const nuevasValidaciones = { ...erroresModal }

    dispatch(setModal({ values: { ...values, [name]: value } }))

    if (erroresModal[name]) {
      delete nuevasValidaciones[name]
      dispatch(setModal({ errores: nuevasValidaciones }))
    }
  }

  const handleConfirm = async () => {
    const errores = {}

    if (!values.provincia || values.provincia === '') {
      errores.provincia = 'Debe seleccionar una provincia.'
    }
    if (!values.condicionFrenteIIBB || values.condicionFrenteIIBB === '') {
      errores.condicionFrenteIIBB = 'Debe seleccionar una condición frente al IIBB.'
    }
    if (!values.esAgente || values.esAgente === '') {
      errores.esAgente = 'Debe seleccionar si es agente de percepción.'
    }

    const existeDuplicado = datosFiscales.find(
      (d) =>
        d.idProvincia === parseInt(values.provincia) &&
        d.idCondicionFrenteIIBB === parseInt(values.condicionFrenteIIBB) &&
        d.esAgentePercepcion === (values.esAgente === 'true') &&
        (!editMode || d.idDatoFiscal !== values.idDatoFiscal)
    )

    if (existeDuplicado) {
      errores.general =
        'Ya existe ese dato fiscal para la provincia, condición y agente seleccionados.'
    }

    dispatch(setModal({ errores }))

    if (Object.keys(errores).length > 0) {
      return false
    }

    try {
      if (editMode) {
        await handleActualizar()
      } else {
        await guardarDatoFiscal()
      }
      if (!existeDatosFiscales) {
        updateUserData({ existeDatosFiscales: !existeDatosFiscales })
      }
      return true
    } catch {
      return false
    }
  }

  const actualizarCheckDatosFiscales = async (aceptaNoHayDatosFiscales) => {
    updateUserData({ aceptaNoHayDatosFiscales: aceptaNoHayDatosFiscales })
    usuariosEBPService.actualizarUsuario({
      idUsuario,
      aceptoNoHayDatosFiscales: aceptaNoHayDatosFiscales
    })
  }

  return (
    <div className={stylesPerfil.container}>
      <h4 className={stylesPerfil.cardTitle}>Datos Fiscales</h4>
      <GenericModal
        modalId="datos-fiscales"
        onConfirm={handleConfirm}
        onChange={handleChangeModal}
      />
      <div>
        <div className={styles.percepcionesGrid}>
          {datosFiscales.length === 0 &&
            (!noEsAdmin ? (
              <div>
                <small className="warningMessage">
                  Si no ingresás los datos fiscales, se te cobrará la alícuota máxima de
                  percepciones que corresponda.
                </small>

                <div className={styles.checkboxWrapper}>
                  <label className={styles.checkboxLabel}>
                    <input
                      className={styles.checkboxInput}
                      type="checkbox"
                      checked={aceptaNoHayDatosFiscales}
                      onChange={(e) => actualizarCheckDatosFiscales(e.target.checked)}
                    />
                    <span>Confirmo que entiendo y acepto esta condición.</span>
                  </label>
                </div>
              </div>
            ) : (
              <div className={styles.infoState}>
                <span>
                  Por favor, comunicate con el administrador para que ingrese los datos fiscales o
                  acepte las condiciones.
                </span>
              </div>
            ))}
          {datosFiscales.map((dato) => {
            return (
              <div key={dato.idDatoFiscal} className={styles.percepcionCard}>
                <div className={styles.percepcionInfo}>
                  <p>
                    <strong>Provincia: </strong>{' '}
                    {provincias.find((p) => p.idProvincia === dato.idProvincia)?.nombre || ''}
                  </p>
                  <p>
                    <strong>Condición frente al IIBB: </strong>{' '}
                    {tiposFiscales.find((t) => t.value === dato.idCondicionFrenteIIBB)?.label || ''}
                  </p>
                  <p>
                    <strong>Es agente de percepción: </strong>{' '}
                    {dato.esAgentePercepcion ? 'Sí' : 'No'}
                  </p>
                </div>
                {esPerfilAdmin && (
                  <div className={styles.accionesRow}>
                    <button
                      type="button"
                      className={`${stylesPerfil.btn} ${stylesPerfil.btnOutlinePrimary} ${styles.btnAccionCuadrado}`}
                      onClick={() => editarDatoFiscal(dato)}
                      title="Editar percepción"
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button
                      type="button"
                      className={`${stylesPerfil.btn} ${stylesPerfil.btnOutlineDanger} ${styles.btnAccionCuadrado}`}
                      onClick={() => eliminarDatoFiscal(dato.idDatoFiscal)}
                      title="Eliminar percepción"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {esPerfilAdmin && (
          <div className={stylesPerfil.btnContainer}>
            <button
              type="button"
              className={`${stylesPerfil.btn} ${stylesPerfil.btnOutlinePrimary} `}
              onClick={abrirModal}
            >
              Agregar dato fiscal
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default DatosFiscales
